import { calculateCompoundedInterest, calculateLinearInterest, rayDiv, rayMul } from '@aave/math-utils'
import {
  BaseNumber,
  bigNumberify,
  NormalizedNumber,
  NumberLike,
  Percentage,
  RAY,
  UnixDuration,
} from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'

import { fromRay, pow, toRay } from '@/utils/math'

interface GetScaledBalanceArgs {
  rate: BigNumber
  index: BigNumber
  timestamp: number
  lastUpdateTimestamp: number
  balance: BaseNumber
}

export function getScaledBalance({
  balance,
  index,
  rate,
  lastUpdateTimestamp,
  timestamp,
}: GetScaledBalanceArgs): BaseNumber {
  const updatedIndex = rayMul(
    calculateLinearInterest({
      currentTimestamp: timestamp,
      lastUpdateTimestamp,
      rate,
    }),
    index,
  )
  return BaseNumber(rayDiv(balance.toBigNumber(), updatedIndex))
}
export function getCompoundedScaledBalance({
  balance,
  index,
  rate,
  lastUpdateTimestamp,
  timestamp,
}: GetScaledBalanceArgs): BaseNumber {
  const updatedIndex = rayMul(
    calculateCompoundedInterest({
      currentTimestamp: timestamp,
      lastUpdateTimestamp,
      rate,
    }),
    index,
  )
  return BaseNumber(rayDiv(balance.toBigNumber(), updatedIndex))
}

export function getCompoundedBalance({
  balance,
  index,
  rate,
  lastUpdateTimestamp,
  timestamp,
}: GetScaledBalanceArgs): BaseNumber {
  const updatedIndex = rayMul(
    calculateCompoundedInterest({
      currentTimestamp: timestamp,
      lastUpdateTimestamp,
      rate,
    }),
    index,
  )
  return BaseNumber(rayMul(balance.toBigNumber(), updatedIndex))
}

export function healthFactorToLtv(healthFactor: BigNumber, liquidationThreshold: Percentage): Percentage {
  return Percentage(1).div(healthFactor.toNumber()).times(liquidationThreshold)
}

export function parseRawPercentage(
  value: NumberLike,
  { allowMoreThan1 }: { allowMoreThan1?: boolean } = { allowMoreThan1: true },
): Percentage {
  return Percentage(bigNumberify(value).div(10_000), { allowMoreThan1 })
}

export interface CalculateVariableBorrowRateParams {
  optimalUtilizationRate: Percentage
  variableRateSlope1: BigNumber
  variableRateSlope2: BigNumber
  baseVariableBorrowRate: BigNumber
  availableLiquidity: NormalizedNumber
  totalVariableDebt: NormalizedNumber
}

export function calculateVariableBorrowRate({
  optimalUtilizationRate,
  variableRateSlope1,
  variableRateSlope2,
  baseVariableBorrowRate,
  availableLiquidity,
  totalVariableDebt,
}: CalculateVariableBorrowRateParams): BigNumber {
  const utilization = Percentage(totalVariableDebt.div(totalVariableDebt.plus(availableLiquidity)))
  return calculateVariableBorrowRateByUtilization({
    optimalUtilizationRate,
    utilization,
    variableRateSlope1,
    variableRateSlope2,
    baseVariableBorrowRate,
  })
}

export interface CalculateVariableBorrowRateByUtilizationParams {
  optimalUtilizationRate: Percentage
  utilization: Percentage
  variableRateSlope1: BigNumber
  variableRateSlope2: BigNumber
  baseVariableBorrowRate: BigNumber
}
export function calculateVariableBorrowRateByUtilization({
  optimalUtilizationRate,
  utilization,
  variableRateSlope1,
  variableRateSlope2,
  baseVariableBorrowRate,
}: CalculateVariableBorrowRateByUtilizationParams): BigNumber {
  const optimalUtilizationRateRay = toRay(optimalUtilizationRate.toBigNumber())
  const utilizationRay = toRay(utilization.toBigNumber())

  if (optimalUtilizationRate.gt(utilization)) {
    const rate = fromRay(
      baseVariableBorrowRate.plus(rayDiv(rayMul(variableRateSlope1, utilizationRay), optimalUtilizationRateRay)),
    )

    return rate
  }
  const excess = RAY().minus(optimalUtilizationRateRay).eq(0)
    ? new BigNumber(0)
    : rayDiv(utilizationRay.minus(optimalUtilizationRateRay), RAY().minus(optimalUtilizationRateRay))
  const rate = fromRay(baseVariableBorrowRate.plus(variableRateSlope1).plus(rayMul(variableRateSlope2, excess)))

  return rate
}

export interface CalculateLiquidityRateParams extends CalculateVariableBorrowRateParams {
  unbacked: NormalizedNumber
  reserveFactor: Percentage
}

export function calculateLiquidityRate({
  optimalUtilizationRate,
  variableRateSlope1,
  variableRateSlope2,
  baseVariableBorrowRate,
  totalVariableDebt,
  availableLiquidity,
  unbacked,
  reserveFactor,
}: CalculateLiquidityRateParams): BigNumber {
  if (totalVariableDebt.eq(0)) {
    return BigNumber(0)
  }

  const variableBorrowRateRay = toRay(
    calculateVariableBorrowRate({
      optimalUtilizationRate,
      availableLiquidity,
      totalVariableDebt,
      variableRateSlope1,
      variableRateSlope2,
      baseVariableBorrowRate,
    }),
  )
  const supplyUsageRatioRay = rayDiv(
    toRay(totalVariableDebt.toBigNumber()),
    toRay(totalVariableDebt.plus(availableLiquidity).plus(unbacked).toBigNumber()),
  )

  return fromRay(rayMul(variableBorrowRateRay, supplyUsageRatioRay)).times(
    Percentage(1).minus(reserveFactor).toBigNumber(),
  )
}

export function rateToYield(rate: BigNumber): Percentage {
  const secondsPerYear = Number(UnixDuration({ years: 1 }).toSeconds())
  return Percentage(pow(new BigNumber(1).plus(rate.div(secondsPerYear)), secondsPerYear).minus(1), {
    allowMoreThan1: true,
  })
}
